import { memo, useCallback, useMemo, useState } from 'react'
import { alpha, useTheme } from '@mui/material'
import Joyride, { STATUS, CallBackProps, LIFECYCLE } from 'react-joyride'
import { TourProps } from './types'
import { ITourContext, TourContextProvider } from './context'
import { TOUR_DEFAULT_OVERLAY_BACKGROUND, TOUR_Z_INDEX } from './constants'
import { SkipButton } from './components/skip-button'
import { Tooltip } from './components/tooltip'

export function Tour({
  onSkip,
  options,
  hideSkipButton = false,
  ...renderTourProps
}: TourProps) {
  const [lifeCycle, setLifeCycle] = useState<CallBackProps['lifecycle']>(
    LIFECYCLE.INIT,
  )

  const tourContext: Partial<ITourContext> = useMemo(() => {
    return {
      getStepNumber: options?.getStepNumber,
      stepNumbersOfLabel: options?.stepNumbersOfLabel,
    }
  }, [options?.stepNumbersOfLabel, options?.getStepNumber])

  return (
    <TourContextProvider value={tourContext}>
      {!hideSkipButton && (
        <SkipButton
          show={lifeCycle === LIFECYCLE.TOOLTIP}
          onClick={onSkip}
          title={options?.skipLabel || 'Skip'}
        />
      )}

      <TourRenderer
        setLifeCycle={setLifeCycle}
        options={options}
        {...renderTourProps}
      />
    </TourContextProvider>
  )
}

const TourRenderer = memo(function TourRendererComponent({
  onComplete,
  steps,
  enabled,
  options,
  overlayStyles,
  setLifeCycle,
  spotLightStyles,
  spotLightPadding,
}: Omit<TourProps, 'onSkip'> & {
  setLifeCycle: (lifeCycle: CallBackProps['lifecycle']) => unknown
}) {
  const theme = useTheme()

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, lifecycle } = data
      setLifeCycle(lifecycle)

      if (status === STATUS.FINISHED) {
        onComplete?.()
      }
    },
    [onComplete, setLifeCycle],
  )

  return (
    <Joyride
      steps={steps}
      run={enabled}
      continuous
      disableCloseOnEsc
      spotlightClicks
      disableOverlayClose
      spotlightPadding={spotLightPadding || 0}
      callback={handleJoyrideCallback}
      locale={{
        next: options?.nextLabel,
        last: options?.doneLabel,
      }}
      tooltipComponent={Tooltip}
      styles={{
        options: {
          arrowColor: alpha(theme.palette.neutral900, 0.8),
          zIndex: TOUR_Z_INDEX,
        },
        overlay: {
          background: TOUR_DEFAULT_OVERLAY_BACKGROUND,
          ...overlayStyles,
        },
        spotlight: {
          borderRadius: '12px',
          ...spotLightStyles,
        },
      }}
    />
  )
})
